/**
 * Validation utilities
 */
import _ from 'lodash'
import ipaddr from 'pw-ipaddr.js'
import {
  splitOutsideQuotes,
  stripCsvSpaces,
  trimString
} from './utils'
import {
  isFQDN,
  isURL,
  isMACAddress
} from './validators'

export { splitOutsideQuotes }
export { stripCsvSpaces }
export { trimString }

const ipAddressUtil = ipaddr.util

const SPACES_REGEX = /^\S*\s/

const URL_OPTIONS = {
  protocols: ['http', 'https', 'ftp'],
  require_tld: true,
  require_protocol: false,
  require_host: true,
  require_valid_protocol: true,
  allow_underscores: true, // Different from validator.js default
  host_whitelist: false,
  host_blacklist: false,
  allow_trailing_dot: false,
  allow_protocol_relative_urls: false
}

const DOMAIN_OPTIONS = {
  require_tld: true,
  allow_underscores: true, // Different from validator.js default
  allow_trailing_dot: false
}

export const INVALID_DATE_TIME_STRING_MSG = 'Invalid Date. Valid formats: ISO-8601, "YYYY-MM-DD hh:mm", "MMM DD, YYYY", etc.'
export const INVALID_FLOAT_MSG = 'Must be an floating point number (must include decimal)'
export const INVALID_INT_MSG = 'Must be an integer (no decimal places)'
export const INVALID_NUMBER_MSG = 'Must be a number'

export const HASH_TYPES_TO_EXPECTED_LENGTH = {
  MD5: 32,
  SHA1: 40,
  SHA256: 64,
  SHA512: 128
}

export function validateIP (val) {
  return (!_.trim(val) || ipAddressUtil.isValidIp(val)) ? null : 'Invalid IP Address'
}

export function validateIPv4 (val) {
  return (!_.trim(val) || ipAddressUtil.isValidIp(val, 4)) ? null : 'Invalid IPv4 Address'
}

export function validateIPv6 (val) {
  return (!_.trim(val) || ipAddressUtil.isValidIp(val, 6)) ? null : 'Invalid IPv6 Address'
}

export function validateCIDR (val) {
  return (!_.trim(val) || ipAddressUtil.isValidCIDR(val)) ? null : 'Invalid CIDR Range'
}

export function validateIPorCIDR (val) {
  return (!_.trim(val) || ipAddressUtil.isValidIp(val) || ipAddressUtil.isValidCIDR(val)) ? null : 'Invalid IP Address or CIDR Range'
}

export function validatePort (val) {
  return /^\d*$/.test(val) && parseInt(val) > 0 <= 65535 ? null : 'Invalid Port'
}

// const MAC_REGEX = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/

export function validateMacAddress (val) {
  // return (!_.trim(val) || MAC_REGEX.test(val)) ? null : 'Invalid MAC Address'
  return isMACAddress(val) ? null : 'Invalid MAC Address'
}

export function validateHash (val, hashType) {
  const valAsString = `${val}`
  const len = valAsString && valAsString.length
  let expectedLen = null
  let invalidMsg = 'Not a valid MD5, SHA1, SHA256, or SHA512 hash'
  if (hashType && Object.prototype.hasOwnProperty.call(HASH_TYPES_TO_EXPECTED_LENGTH, hashType)) {
    expectedLen = HASH_TYPES_TO_EXPECTED_LENGTH[hashType]
    invalidMsg = `Not a valid ${hashType} hash`
  }
  return (!len || ((expectedLen ? (len === expectedLen) : (len === 32 || len === 40 || len === 64 || len === 128)) && /^[0-9a-f]+$/i.test(valAsString))) ? null : invalidMsg
}

export function validateHashType (val/*, hashType */) {
  const valAsString = `${val}`

  const len = valAsString && valAsString.length

  return (!len || ((len === 32 || len === 40 || len === 64 || len === 128) && /^[0-9a-f]+$/i.test(valAsString))) ? null : 'Not a valid MD5, SHA1, SHA256, or SHA512 hash'
}

export function validateDomain (val) {
  // const domainRegex = /^(?:(?:[_a-zA-Z0-9]){0,61}(?:[_a-zA-Z0-9-\.]){0,61}[_a-zA-Z0-9](?:\.[a-zA-Z]{2,})+|\[(?:(?:(?:[a-fA-F0-9]){1,4})(?::(?:[a-fA-F0-9]){1,4}){7}|::1|::)\]|(?:(?:[0-9]{1,3})(?:\.[0-9]{1,3}){3}))(?:\:[0-9]{1,5})?$/ig // eslint-disable-line no-useless-escape
  // return domainRegex.test(val) ? null : "Invalid Domain -- Valid samples: example.com, www.example.net"
  return isFQDN(val, DOMAIN_OPTIONS) ? null : 'Invalid Domain -- Valid samples: example.com, www.example.net'
}

// Copyright (c) 2010-2013 Diego Perini, MIT licensed
// https://gist.github.com/dperini/729294
// see also https://mathiasbynens.be/demo/url-regex
// modified to allow protocol-relative URLs
export function validateURL (val, noProtocol = false) {
  // let urlRegex = /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})).?)(?::\d{2,5})?(?:[/?#]\S*)?$/i
  // if (noProtocol) {
  //   urlRegex = /^(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})).?)(?::\d{2,5})?(?:[/?#]\S*)?$/i
  // }
  // return urlRegex.test(val) ? null : `Invalid URL -- Valid samples: ${ noProtocol ? "" : "http://" }example.com, ${ noProtocol ? "" : "https://" }www.example.net`
  return isURL(val, URL_OPTIONS) ? null : `Invalid URL -- Valid samples: ${noProtocol ? '' : 'http://'}example.com, ${noProtocol ? '' : 'https://'}www.example.net`
}

export function validateNonEmptyString (input) {
  return !input || input.length === 0 ? 'Required' : null
}

export function validateNoSpaces (input) {
  return SPACES_REGEX.test(input) ? 'Spaces are not allowed' : null
}

export function validateNumber (input) {
  return isNaN(+input) ? INVALID_NUMBER_MSG : null
}

export function validateInt (input) {
  // must also return invalid for numbers that are valid ints (e.g. 1.00)
  return validateNumber(input) || (input == null || input % 1 === 0 ? null : INVALID_INT_MSG)
}

export function validateFloat (input) {
  return validateNumber(input) || (!input || input % 1 !== 0 ? null : INVALID_FLOAT_MSG)
}

export function validatePositiveNumber (input) {
  return validateNumber(input) || (+input < 0 ? 'Must be a positive number' : null)
}

export function validatePositiveInt (input) {
  return validateInt(input) || (+input < 0 ? 'Must be a positive integer (no decimal places)' : null)
}

function generateNumericMinMaxValidator (numberType = 'number') {
  const baseValidator = numberType === 'float' ? validateFloat : (numberType === 'integer' ? validateInt : validateNumber)

  return function (min = null, max = null) {
    if (min !== null && max !== null) {
      return function (input) {
        return baseValidator(input) || (+input >= min && +input <= max ? null : `Must be ${numberType === 'integer' ? 'an' : 'a'} ${numberType} between ${min.toLocaleString()} and ${max.toLocaleString()}`)
      }
    } else if (min !== null) {
      return function (input) {
        return baseValidator(input) || (+input >= min ? null : `Must be ${numberType === 'integer' ? 'an' : 'a'} ${numberType} greater than ${min.toLocaleString()}`)
      }
    } else if (max !== null) {
      return function (input) {
        return baseValidator(input) || (+input <= max ? null : `Must be ${numberType === 'integer' ? 'an' : 'a'} ${numberType} less than ${max.toLocaleString()}`)
      }
    }
  }
}

export const getNumberRangeValidator = generateNumericMinMaxValidator('number')
export const getIntRangeValidator = generateNumericMinMaxValidator('integer')
export const getFloatRangeValidator = generateNumericMinMaxValidator('float')

// export function getIntRangeValidator (min = null, max = null) {
//   if (min !== null && max !== null) {
//     return function (input) {
//       return validateInt(input) || (+input >= min && +input <= max ? null : `Must be an integer between ${ min.toLocaleString() } and ${ max.toLocaleString() }`)
//     }
//   }
//   else if (min !== null) {
//     return function (input) {
//       return validateInt(input) || (+input >= min ? null : `Must be an integer greater than ${ min.toLocaleString() }`)
//     }

//   }
//   else if (max !== null) {
//     return function (input) {
//       return validateInt(input) || (+input <= max ? null : `Must be an integer less than ${ max.toLocaleString() }`)
//     }
//   }
// }

// export function getNumberRangeValidator (min, max) {
//   return function (input) {
//     return validateNumber(input) || (+input >= min && +input <= max ? null : `Must be between ${ min.toLocaleString() } and ${ max.toLocaleString() }`)
//   }
// }

/**
 * Return a validator function for testing string length, with optional min/max
 * @param {*number or null} min Min Length. If null is passed function will not validate for minlength
 * @param {*number or null} max Max Length. If null is passed function will not validate for maxlength
 */
export function getStringLengthValidator (min = null, max = null) {
  if (min !== null && max !== null) {
    return function (input) {
      return input && input.length >= min && input.length <= max ? null : `Must be between ${min.toLocaleString()} and ${max.toLocaleString()} characters.`
    }
  } else if (min !== null) {
    return function (input) {
      return input && input.length >= min ? null : `Must be at least ${min.toLocaleString()} characters.`
    }
  } else if (max !== null) {
    return function (input = '') {
      return input.length <= max ? null : `Must not exceed ${max.toLocaleString()} characters.`
    }
  } else {
    throw new Error('getStringLengthValidator requires at least `min` or `max` specified')
  }
}

export function validateThisOptions (value) {
  return this.options && this.options.indexOf(value) === -1 ? `Must be one of: ${this.options.join(', ')}` : null
}

export function getEnumValidator (enumProp, caseInsensitive = true, displayOptsProp) {
  return function (value) {
    const opts = this[enumProp]
    const displayOpts = displayOptsProp ? this[displayOptsProp] : opts
    const passCount = [] // prevent duplicate enums
    if (!opts) {
      return null
    }
    const values = splitOutsideQuotes(value, ',') // Split multi-values
    let val = values.length > 1 ? values : [{ match: value }]

    // Wrapping quotes should not interfere with validation
    val = val.map(v => {
      return v.match && v.match.replace ? { match: v.match.replace(/"/gi, '') } : v
    })

    const validationOutput = []

    for (var i = 0; i < val.length; i++) {
      const thisValString = typeof val[i].match === 'string' ? val[i].match : val[i].match.name // caseInsensitive ? val[i].match.toLowerCase() : val[i].match
      const thisVal = (caseInsensitive ? thisValString.toLowerCase() : thisValString).trim()
      if (opts && opts.indexOf(thisVal) === -1) {
        return `"${thisVal}" Must be one of these options: ${displayOpts.join(', ')}.`
      } else if (passCount.indexOf(thisVal) !== -1) {
        return `"${thisVal}" may only be used once per clause.`
      } else {
        passCount.push(thisVal)
      }
    }
    return validationOutput.length > 0 ? validationOutput.join('\n') : null
  }
}

export function validateFileSize (val) {
  return /[a-z]/i.test(val) ? 'Must be a numerical value in bytes' : null
}

// const DEFAULT_FQDN_OPTIONS = {
//   require_tld: true,
//   allow_underscores: false,
//   allow_trailing_dot: false,
// }

// determine if a string is a fully qualified domain name
// https://github.com/chriso/validator.js/blob/master/src/lib/isFQDN.js
export function isFullyQualifiedDomainName (str, options = {}) {
  if (!_.isString(str)) {
    return false
  }
  return isFQDN(str, options)
}

/**
 * Validate a string. Anything that passes coercion/parsing by the Javascript new Date() method is considered valid.
 * Samples:
 *  - Full ISO8601: YYYY-MM-DDThh:mm:ss:000Z
 *  - Partial date:  2006-10-01
 *  - Partial date with hours/minutes: 2004-04-20 4:30
 *  - Different date format: April 20, 2001
 * @param {string} input
 */
export function validateDateTimeString (input) {
  return !!input && isNaN(+new Date(input)) ? INVALID_DATE_TIME_STRING_MSG : null
}
